/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { Divider, Menu, Space, Tooltip, message } from 'antd';
import classnames from 'classnames';
import { uniqBy } from 'lodash';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useDepartment from '@/hooks/useDepartment';
import { useApp } from '@/provider/app';
import { useAppDispatch } from '@/stores/hooks';
import { showPopup } from '@/stores/slices/appPopup';
import { userSelector } from '@/stores/slices/user';
import AppUtil from '@/utils/AppUtil';
import { DepartmentPermissionsEnum } from '@/utils/permissionConstants';
import routes from '@/utils/routes';
import AppConstants from '@utils/AppConstants';

import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';

import {
  Department,
  ListDepartmentsQueryVariables,
  useListDepartmentsLazyQuery
} from '@/generated/graphql';
import AccessControl from '../AccessControl/AccessControl';
import DebouncedInput from '../DebouncedInput/DebouncedInput';

import styles from './DepartmentSelectorMenu.module.less';

interface IProps {
  hide: () => void;
  departments: Department[];
  departmentSearchText: string;
  setDepartmentSearchText: React.Dispatch<React.SetStateAction<string>>;
  setDepartments: React.Dispatch<React.SetStateAction<Department[]>>;
}

function DepartmentSelectorMenu(props: IProps) {
  const { setDepartment: setDeptInURL } = useApp();

  const {
    hide,
    departments,
    setDepartments,
    departmentSearchText,
    setDepartmentSearchText
  } = props;

  const { organization, username } = useSelector(userSelector);
  const { department } = useDepartment();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const canCreateDepartment = AppUtil.canCreateDepartment(
    username,
    organization.data
  );

  const [fetchDepartmentsQuery, { loading }] = useListDepartmentsLazyQuery({
    onError(error) {
      message.error(error.message);
    }
  });

  const fetchDepartments = async (
    searchValue: string | null
  ): Promise<Department[]> => {
    setDepartmentSearchText(searchValue || '');
    const variables: ListDepartmentsQueryVariables = {
      limit: 10
    };
    if (searchValue) {
      variables.deptFilter = {
        name: searchValue
      };
    }

    const query = await fetchDepartmentsQuery({
      variables
    });

    const deps = (query.data?.listDepartments?.items || []) as Department[];
    setDepartments(deps);
    return deps;
  };

  useEffect(() => {
    if (departments.length === 0) {
      fetchDepartments(null);
    }
  }, []);

  const handleDepartmentChange = async (dep: Department) => {
    await setDeptInURL(dep);
    hide();
  };

  const departmentsToShow = departmentSearchText
    ? departments
    : uniqBy([department, ...departments].filter(Boolean), 'id');

  const renderContent = () => {
    if (loading) {
      return (
        <Menu.Item className={styles.menuItem} key="none" disabled>
          <Space>
            <LoadingOutlined /> Loading...
          </Space>
        </Menu.Item>
      );
    }

    if (departments.length === 0) {
      <Menu.Item className={styles.menuItem} key="none" disabled>
        No Departments
      </Menu.Item>;
    }

    return departmentsToShow
      .map((dept) => {
        const content = (
          <Menu.Item
            className={classnames([
              styles.menuItem,
              { [styles.activeItem]: department.id === dept!.id }
            ])}
            data-cy={dept!.name}
            key={dept!.id}
            onClick={() => {
              handleDepartmentChange(dept!);
            }}
          >
            {dept!.name}
          </Menu.Item>
        );
        return dept!.name!.length > 32 ? (
          <Tooltip placement="left" color="#303048" title={dept!.name}>
            {content}
          </Tooltip>
        ) : (
          content
        );
      })
      .slice(0, 10);
  };

  return (
    <span className={styles.departmentMenu}>
      <Menu
        className={styles.menuContainer}
        {...{
          'data-cy': loading ? 'department-selector-content-spinner' : undefined
        }}
      >
        <span className={styles.topMenu} key="search-input">
          <DebouncedInput
            prefix={
              <SearchOutlined
                style={{ color: '#C2C2D0', marginRight: '8px' }}
              />
            }
            value={departmentSearchText}
            className={styles.departmentSearch}
            size="middle"
            placeholder="Search departments"
            onChange={(value) => {
              if (value === '') {
                fetchDepartments(null);
              } else {
                fetchDepartments(value);
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            autoFocus
            spellCheck={false}
            autoComplete="off"
            dataCy="departments-selector-search"
            allowClear
          />
          <Divider className={styles.menuDivider} />
          {renderContent()}
        </span>
        <span className={styles.bottomMenu}>
          <Divider className={styles.menuDivider} />
          {canCreateDepartment && (
            <AccessControl
              requiredPermissions={[DepartmentPermissionsEnum.DepartmentCreate]}
            >
              <Menu.Item
                className={styles.menuItem}
                key="create-new"
                data-cy="create-new-department"
                onClick={() => {
                  dispatch(
                    showPopup({
                      popupId: AppConstants.POPUPS.CREATE_DEPARTMENT,
                      popupParams: {
                        onSuccess: handleDepartmentChange
                      }
                    })
                  );
                  hide();
                }}
              >
                Create New
              </Menu.Item>
            </AccessControl>
          )}
          <AccessControl
            requiredPermissions={[DepartmentPermissionsEnum.DepartmentRead]}
          >
            <Menu.Item
              className={styles.menuItem}
              key="view-all"
              data-cy="department-selector-view-all"
              onClick={() => {
                navigate(
                  routes.department.getDepartmentsPageUrl(department.id)
                );
                hide();
              }}
            >
              View All
            </Menu.Item>
          </AccessControl>
        </span>
      </Menu>
    </span>
  );
}

export default DepartmentSelectorMenu;
